import { getElementBounds } from '../../lib/bound';
import { getCursorForResizingElement } from '../../lib/cursor';
import { reversionElement, updateElement } from '../../lib/element';
import {
  isInsideTransformHandle,
  type TransformHandleType,
} from '../../lib/selection';
import { rescalePoints } from '../../lib/shapes';
import { moveMultipleElements } from '../../lib/transformation';
import { useEditorStore } from '../../stores/useEditorStore';
import { normalizeRadians, pointRotateRads } from '../../utils/math';

import { getPolyBounds } from './utils/bounds';
import { getPolyTransformHandles, resizePoly } from './utils/transform';
import {
  rotatePoly,
  translatePoint,
  translatePoints,
} from './utils/translation';

import { type GenericPolyElement, type PolyPlugin } from '.';

export const transformation: PolyPlugin['transformation'] = {
  shiftBySizeOnFlip: false,
  onSingleResize: (_, initial, nextWidth, nextHeight, direction) => {
    const offsetDirectionX = direction.includes('w') ? -1 : 1;
    const offsetDirectionY = direction.includes('n') ? -1 : 1;

    const offsetX = nextWidth - initial.width;
    const offsetY = nextHeight - initial.height;

    const updated = resizePoly(
      direction,
      initial,
      [offsetX * offsetDirectionX, offsetY * offsetDirectionY],
      [0, 0],
    );
    return updated;
  },
  onMultiResize: (_, initial, nextWidth, nextHeight, direction) => {
    const offsetDirectionX = direction.includes('w') ? -1 : 1;
    const offsetDirectionY = direction.includes('n') ? -1 : 1;

    const offsetX = nextWidth - initial.width;
    const offsetY = nextHeight - initial.height;

    const updated = resizePoly(
      direction,
      initial,
      [offsetX * offsetDirectionX, offsetY * offsetDirectionY],
      [0, 0],
    );
    return {
      points: updated.points,
      bezierHandles: updated.bezierHandles,
      width: nextWidth,
      height: nextHeight,
    };
  },
  resizedBounds: (element, newWidth, newHeight) => {
    const resizedPoints = rescalePoints(
      0,
      newWidth,
      rescalePoints(1, newHeight, element.points, true),
      true,
    );
    // TODO check whether we need bezier consideration
    return getPolyBounds(resizedPoints, element.x, element.y);
  },
  resizeTestFromBounds: (element, [cursorX, cursorY], lib) => {
    const handles = getPolyTransformHandles!(element, lib);

    const found = Object.keys(handles).find((key) => {
      return isInsideTransformHandle(handles[key], cursorX, cursorY);
    });

    return found ? found : null;
  },
  getCursorFromTransformation: (type) => {
    if (!isNaN(Number(type))) return 'pointer';
    return getCursorForResizingElement(type as TransformHandleType);
  },
  transformElement: (
    transform,
    element,
    original,
    [pointerX, pointerY],
    [originX, originY],
    { elementState },
  ) => {
    const originalElement = original.get(element.id) as GenericPolyElement;

    if (transform === 'rotation') {
      const [, , , , cx, cy] = getElementBounds(originalElement);
      const angle = normalizeRadians(
        (5 * Math.PI) / 2 + Math.atan2(pointerY - cy, pointerX - cx),
      );

      const updated = rotatePoly(originalElement, angle);
      updateElement<GenericPolyElement>(
        elementState.elements,
        element,
        updated,
      );
      return;
    }

    if (transform === 'move') {
      moveMultipleElements([element], [pointerX, pointerY], [originX, originY]);

      return;
    }

    if (
      !transform.includes('handle') &&
      ['w', 'e', 's', 'n'].some((dir) => transform.includes(dir))
    ) {
      const updated = resizePoly(
        transform,
        originalElement,
        [pointerX, pointerY],
        [originX, originY],
      );
      updateElement<GenericPolyElement>(
        elementState.elements,
        element,
        updated,
      );
      return;
    }

    const transformPointIndex = Number.parseInt(transform.split('-')[0], 10);
    const { selectedSubElementIds } = useEditorStore.getState();

    const isMultiPointMove =
      selectedSubElementIds.length > 1 &&
      selectedSubElementIds.includes(transformPointIndex.toString()) &&
      !transform.includes('handle');

    if (isMultiPointMove) {
      const pointIndices = selectedSubElementIds.map((id) =>
        Number.parseInt(id, 10),
      );
      const updated = translatePoints(
        pointIndices,
        element,
        originalElement,
        [pointerX, pointerY],
        [originX, originY],
      );
      updateElement<GenericPolyElement>(
        elementState.elements,
        element,
        updated,
      );
      return;
    }

    useEditorStore.setState({
      selectedSubElementIds: [transformPointIndex.toString()],
    });

    const updated = translatePoint(
      transformPointIndex,
      element,
      originalElement,
      [pointerX, pointerY],
      [originX, originY],
      transform.includes('handleBefore'),
      transform.includes('handleAfter'),
    );
    updateElement<GenericPolyElement>(elementState.elements, element, updated);
  },
  manualRotation: (element, originalElement, center, centerAngle) => {
    const updated = rotatePoly(originalElement, centerAngle);

    const [newX, newY] = pointRotateRads(
      [originalElement.x, originalElement.y],
      [center[0], center[1]],
      centerAngle,
    );

    return {
      ...element,
      ...updated,
      x: newX,
      y: newY,
      ...reversionElement(element, ['bounds', 'canvas']),
    };
  },
};
